import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import DashboardPage from './pages/dashboard/DashboardPage';
import WorkspacePage from './pages/workspace/WorkspacePage';
import ApplicationsPage from './pages/applications/ApplicationsPage';
import ApplicationViewPage from './pages/applications/ApplicationViewPage';
import RoutesPage from './pages/routes/RoutesPage';
import RouteCardPage from './pages/routes/RouteCardPage';
import DriverSchedulePage from './pages/operations/DriverSchedulePage';
import WarehousesPage from './pages/warehouses/WarehousesPage';
import CargoPage from './pages/cargo/CargoPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import ReferencesPage from './pages/references/ReferencesPage';
import ContractorCardPage from './pages/contractors/ContractorCardPage';
import CarrierCardPage from './pages/carriers/CarrierCardPage';
import DriverCardPage from './pages/drivers/DriverCardPage';
import VehicleCardPage from './pages/vehicles/VehicleCardPage';
import ReportsPage from './pages/reports/ReportsPage';
import AdministrationPage from './pages/administration/AdministrationPage';
import UsersPage from './pages/administration/UsersPage';
import RolesPage from './pages/administration/RolesPage';
import RolesAccessPage from './pages/administration/RolesAccessPage';
import AuditPage from './pages/administration/AuditPage';
import SettingsPage from './pages/SettingsPage';
import DriversVehiclesPage from './pages/driversVehicles/DriversVehiclesPage';
import ProfilePage from './pages/profile/ProfilePage';
import TNPage from './pages/tn/TNPage';
import TNEditPage from './pages/tn/TNEditPage';
import POAPage from './pages/poa/POAPage';
import POAEditPage from './pages/poa/POAEditPage';
import FinancePage from './pages/finance/FinancePage';
import FinanceWorkspacePage from './pages/finance/FinanceWorkspacePage';
import FinanceDashboardPage from './pages/finance/FinanceDashboardPage';
import FinanceInvoicesPage from './pages/finance/FinanceInvoicesPage';
import FinancePaymentsPage from './pages/finance/FinancePaymentsPage';
import FinanceReceivablesPage from './pages/finance/FinanceReceivablesPage';
import FinancePayablesPage from './pages/finance/FinancePayablesPage';
import CalculationsListPage from './pages/calculations/CalculationsListPage';
import UnifiedCalculationPage from './pages/calculations/UnifiedCalculationPage';
import PartnerApplicationsPage from './pages/partner/PartnerApplicationsPage';
import CostAnalyticsPage from './pages/analytics/CostAnalyticsPage';
import DeliveryCostCalculatorPage from './pages/tools/DeliveryCostCalculatorPage';
import LogistsControlPage from './pages/control/LogistsControlPage';
import CarriersControlPage from './pages/control/CarriersControlPage';
import ClientsControlPage from './pages/control/ClientsControlPage';
import LoginPage from './pages/auth/LoginPage';
import { AdminOutlet, FinanceViewOutlet } from './components/routing/RouteGuards';

const AuthenticatedAppRoutes: React.FC = () => {
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | undefined;
  const background = state?.backgroundLocation;

  const referenceEntityCardRoutes = (
    <>
      <Route path="/contractors/:id" element={<ContractorCardPage />} />
      <Route path="/clients/:id" element={<ContractorCardPage />} />
      <Route path="/carriers/:id" element={<CarrierCardPage />} />
      <Route path="/drivers/:id" element={<DriverCardPage />} />
      <Route path="/vehicles/:id" element={<VehicleCardPage />} />
    </>
  );

  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/" element={<Navigate to="/workspace" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/workspace" element={<WorkspacePage />} />
        <Route path="/applications/:id" element={<ApplicationViewPage />} />
        <Route path="/applications" element={<ApplicationsPage />} />
        <Route path="/routes/:id" element={<RouteCardPage />} />
        <Route path="/routes/*" element={<RoutesPage />} />
        <Route path="/operations/driver-schedule" element={<DriverSchedulePage />} />
        <Route path="/warehouses/*" element={<WarehousesPage />} />
        <Route path="/cargo" element={<CargoPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/poa" element={<POAPage />} />
        <Route path="/poa/:id" element={<POAEditPage />} />
        <Route path="/tn" element={<TNPage />} />
        <Route path="/tn/new" element={<TNEditPage />} />
        <Route path="/tn/:id/view" element={<TNEditPage />} />
        <Route path="/tn/:id" element={<TNEditPage />} />
        <Route element={<FinanceViewOutlet />}>
          <Route path="/finance" element={<FinancePage />} />
          <Route path="/finance/dashboard" element={<FinanceDashboardPage />} />
          <Route path="/finance/workspace" element={<FinanceWorkspacePage />} />
          <Route path="/finance/invoices" element={<FinanceInvoicesPage />} />
          <Route path="/finance/payments" element={<FinancePaymentsPage />} />
          <Route path="/finance/receivables" element={<FinanceReceivablesPage />} />
          <Route path="/finance/payables" element={<FinancePayablesPage />} />
          <Route path="/control/logists" element={<LogistsControlPage />} />
          <Route path="/control/carriers" element={<CarriersControlPage />} />
          <Route path="/control/clients" element={<ClientsControlPage />} />
        </Route>
        <Route path="/partner-applications" element={<PartnerApplicationsPage />} />
        <Route path="/calculations" element={<CalculationsListPage />} />
        <Route path="/calculations/:id" element={<UnifiedCalculationPage />} />
        <Route path="/analytics/cost" element={<CostAnalyticsPage />} />
        <Route path="/tools/delivery-cost" element={<DeliveryCostCalculatorPage />} />
        {referenceEntityCardRoutes}
        <Route path="/references/*" element={<ReferencesPage />} />
        <Route path="/drivers-vehicles" element={<DriversVehiclesPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route element={<AdminOutlet />}>
          <Route path="/administration" element={<AdministrationPage />} />
          <Route path="/administration/users" element={<UsersPage />} />
          <Route path="/administration/roles-access" element={<RolesAccessPage />} />
          <Route path="/administration/roles" element={<RolesPage />} />
          <Route path="/administration/audit" element={<AuditPage />} />
        </Route>
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      {background ? <Routes>{referenceEntityCardRoutes}</Routes> : null}
    </>
  );
};

export default AuthenticatedAppRoutes;
